// frontend/components/settings/AdminMemberTab.tsx

"use client";

import { useState, useEffect, useCallback } from "react";
import { UserPlus, Pencil, Bell, CheckCheck } from "lucide-react";
import { useAdminMembers } from "../../hooks/useAdminMembers";
import CreateMemberModal from "./CreateMemberModal";
import EditMemberModal from "./EditMemberModal";
import MaskedSecret from "../common/MaskedSecret";
import { API_URL, authHeaders } from "../../utils/api";

interface AdminMemberTabProps {
  session: any;
}

interface ChangeRequest {
  id: string;
  userId: string;
  department: string | null;
  position: string | null;
  isDeptHead: boolean | null;
  createdAt: string;
  user: {
    id: string;
    loginId: string;
    name: string;
    department: string | null;
    position: string | null;
    isDeptHead: boolean;
    role: string;
  };
}

export default function AdminMemberTab({ session }: AdminMemberTabProps) {
  const accessToken = (session?.user as any)?.accessToken;

  const { members, fetchMembers, createMember, resetPassword, updateMember, deleteMember, createdInfo, setCreatedInfo } =
    useAdminMembers(session?.user?.role, accessToken, API_URL);

  const [isAddModalOpen, setIsAddModalOpen]   = useState(false);
  const [editingMember, setEditingMember]     = useState<any>(null);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);

  // 변경 요청 목록
  const [changeRequests, setChangeRequests]   = useState<ChangeRequest[]>([]);
  const [requestsOpen, setRequestsOpen]       = useState(false);

  const fetchRequests = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_URL}/system/requests`, { headers: authHeaders(accessToken) });
      if (res.ok) setChangeRequests(await res.json());
    } catch {}
  }, [accessToken]);

  useEffect(() => {
    fetchMembers();
    fetchRequests();
  }, [fetchMembers, fetchRequests]);

  const handleOpenRequest = (req: ChangeRequest) => {
    // EditMemberModal에 현재 사용자 정보를 기반으로 요청된 값을 pre-fill
    setEditingMember({
      id: req.user.id,
      loginId: req.user.loginId,
      name: req.user.name,
      department: req.department ?? req.user.department,
      position: req.position ?? req.user.position,
      isDeptHead: req.isDeptHead ?? req.user.isDeptHead,
      role: req.user.role,
    });
    setPendingRequestId(req.id);
  };

  const handleSaveWithDone = async (memberId: string, data: any) => {
    const ok = await updateMember(memberId, data);
    if (ok && pendingRequestId) {
      // 저장 성공 시 요청 처리 완료 표시
      await fetch(`${API_URL}/system/requests/${pendingRequestId}/done`, {
        method: "PATCH",
        headers: authHeaders(accessToken),
      }).catch(() => {});
      setPendingRequestId(null);
      fetchRequests();
    }
    return ok;
  };

  const handleCloseEdit = () => {
    setEditingMember(null);
    setPendingRequestId(null);
  };

  return (
    <div className="space-y-4 relative min-h-87.5">

      {/* 탭 헤더 및 신규 계정 발급 버튼 */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-gray-400 tracking-wide uppercase">
          소속 멤버 목록 ({members.length}명)
        </p>
        <div className="flex items-center gap-2">
          {/* 변경 요청 알림 */}
          <button
            onClick={() => setRequestsOpen(v => !v)}
            className={`relative flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              requestsOpen
                ? "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400"
                : "border-gray-200 dark:border-zinc-700 text-gray-500 dark:text-zinc-400 hover:bg-gray-50 dark:hover:bg-zinc-800"
            }`}
          >
            <Bell size={13} />
            변경 요청
            {changeRequests.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full leading-none">
                {changeRequests.length}
              </span>
            )}
          </button>

          <button
            onClick={() => { setCreatedInfo(null); setIsAddModalOpen(true); }}
            className="px-3 py-1.5 bg-[#006DBA] hover:bg-[#005A9E] text-white text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors shadow-sm"
          >
            <UserPlus size={14} /> <span>신규 계정 발급</span>
          </button>
        </div>
      </div>

      {/* 변경 요청 목록 */}
      {requestsOpen && (
        <div className="border border-amber-200 dark:border-amber-800/60 rounded-lg overflow-hidden bg-amber-50/50 dark:bg-amber-950/10">
          {changeRequests.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-400 dark:text-zinc-500">
              <CheckCheck size={14} />
              처리할 변경 요청이 없습니다.
            </div>
          ) : (
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-amber-100/60 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-semibold border-b border-amber-200 dark:border-amber-800/60">
                  <th className="p-3">요청자</th>
                  <th className="p-3">요청 내용</th>
                  <th className="p-3 text-right">처리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100 dark:divide-amber-800/40 text-gray-700 dark:text-zinc-300">
                {changeRequests.map(req => (
                  <tr key={req.id} className="hover:bg-amber-50 dark:hover:bg-amber-950/20">
                    <td className="p-3">
                      <p className="font-mono font-medium text-[#006DBA] dark:text-[#3399FF]">{req.user.loginId}</p>
                      <p className="text-gray-500 dark:text-zinc-400 mt-0.5">{req.user.name || "-"}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-gray-400 dark:text-zinc-500 text-[11px] mb-0.5">현재</p>
                      <p className="text-gray-600 dark:text-zinc-300">
                        {[req.user.department, req.user.position, req.user.isDeptHead ? "부서장" : null].filter(Boolean).join(" · ") || "미설정"}
                      </p>
                      <p className="text-gray-400 dark:text-zinc-500 text-[11px] mb-0.5 mt-1">→ 요청</p>
                      <p className="text-amber-700 dark:text-amber-400 font-medium">
                        {[req.department, req.position, req.isDeptHead ? "부서장" : (req.isDeptHead === false ? "(부서장 해제)" : null)].filter(Boolean).join(" · ") || "변경 없음"}
                      </p>
                    </td>
                    <td className="p-3 text-right align-middle">
                      <button
                        onClick={() => handleOpenRequest(req)}
                        className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-md transition-colors border border-transparent hover:border-amber-200 dark:hover:border-amber-800"
                        title="요청 내용으로 편집 열기"
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 비밀번호 초기화 완료 알림 */}
      {createdInfo && createdInfo.isReset && (
        <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg text-xs space-y-1 text-red-800 dark:text-red-400 mb-2">
          <p className="font-bold">비밀번호 강제 초기화 완료</p>
          <p>해당 팀원은 다음 로그인 시 반드시 비밀번호를 변경해야 합니다.</p>
          <p>계정 ID: <span className="font-mono bg-white dark:bg-black/40 px-1 py-0.5 rounded border">{createdInfo.loginId}</span></p>
          <p className="flex items-center">임시 암호: <MaskedSecret value={createdInfo.tempPass} className="text-red-600 dark:text-red-400 ml-1" /></p>
        </div>
      )}

      {/* 멤버 목록 테이블 */}
      <div className="border border-gray-100 dark:border-zinc-800 rounded-lg overflow-hidden max-h-[45vh] overflow-y-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-zinc-950 text-gray-400 font-semibold border-b border-gray-100 dark:border-zinc-800">
              <th className="p-3">계정(ID) / 권한</th>
              <th className="p-3">이름 / 부서</th>
              <th className="p-3 text-right">관리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800 text-gray-700 dark:text-zinc-300">
            {members.map((m) => (
              <tr key={m.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                <td className="p-3">
                  <p className="font-mono font-medium text-[#006DBA] dark:text-[#3399FF]">{m.loginId}</p>
                  <div className="mt-1 flex space-x-1">
                    {m.role === "ADMIN" && <span className="text-[10px] bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded font-medium border border-red-200 dark:border-red-800/30">최고관리자</span>}
                    {m.role === "USER"  && <span className="text-[10px] bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded font-medium border border-blue-200 dark:border-blue-800/30">내부직원</span>}
                    {m.role === "GUEST" && <span className="text-[10px] bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium border border-gray-200 dark:border-zinc-700">외부인</span>}
                  </div>
                </td>
                <td className="p-3">
                  <p className="font-bold text-sm">{m.name || "-"}</p>
                  <p className="text-gray-400 mt-0.5">
                    {[m.department, m.position, m.isDeptHead ? "부서장" : ""].filter(Boolean).join(" · ") || "-"}
                  </p>
                </td>
                <td className="p-3 text-right align-middle">
                  {m.role !== "ADMIN" && (
                    <button
                      onClick={() => { setCreatedInfo(null); setPendingRequestId(null); setEditingMember(m); }}
                      className="p-2 text-gray-400 hover:text-[#006DBA] hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-md transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-900"
                      title="멤버 정보 수정"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 신규 계정 발급 모달 */}
      <CreateMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCreate={createMember}
        createdInfo={createdInfo}
      />

      {/* 멤버 정보 수정 모달 */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          onClose={handleCloseEdit}
          onSave={pendingRequestId ? handleSaveWithDone : updateMember}
          onResetPassword={(loginId) => { resetPassword(loginId); handleCloseEdit(); }}
          onDeleteMember={deleteMember}
        />
      )}
    </div>
  );
}
